/**
 * Informed OS Academy — AssessmentQuestion (Phase 1d).
 *
 * Semantic form controls for single-choice / multi-select / true-false
 * questions. Keyboard-navigable (Tab between questions, Space/Enter to
 * toggle). Never renders answer-key state — the caller only receives
 * the sanitized learner payload from the server.
 */
export default function AssessmentQuestion({ index, question, selection, onChange }) {
  const inputType = question.kind === "multi_choice" ? "checkbox" : "radio";
  const groupName = `q-${question.id}`;

  const toggle = (optionId) => {
    if (inputType === "checkbox") {
      const set = new Set(selection || []);
      if (set.has(optionId)) set.delete(optionId);
      else set.add(optionId);
      onChange(Array.from(set));
    } else {
      onChange([optionId]);
    }
  };

  return (
    <fieldset
      data-testid={`academy-assessment-question-${question.id}`}
      className="rounded-xl border border-v2-border bg-v2-surface p-6"
    >
      <legend className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted mb-2">
        Question {index + 1}
        {question.kind === "multi_choice" ? (
          <span className="ml-2 text-v2-accent">Select all that apply</span>
        ) : null}
      </legend>
      <p
        data-testid={`academy-assessment-question-prompt-${question.id}`}
        className="text-v2-text whitespace-pre-wrap"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {question.prompt_markdown}
      </p>
      <div className="mt-4 space-y-2">
        {(question.options || []).map((opt) => {
          const checked = (selection || []).includes(opt.id);
          return (
            <label
              key={opt.id}
              data-testid={`academy-assessment-option-${question.id}-${opt.id}`}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                checked
                  ? "border-[var(--v2-accent)] bg-[var(--v2-accent-soft)]"
                  : "border-v2-border bg-v2-background hover:border-v2-accent"
              }`}
            >
              <input
                type={inputType}
                name={groupName}
                value={opt.id}
                checked={checked}
                onChange={() => toggle(opt.id)}
                className="mt-1"
              />
              <span className="text-sm text-v2-text">{opt.text}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
